import React from 'react';
import {
  reduxForm,
  Field,
  registerField,
  change as changeField,
  unregisterField,
} from 'redux-form';
import Recaptcha from 'react-recaptcha';
import InputField from './InputField';
import FormError from './FormError';
import Spinner from './Spinner';
import { recapthcaKey } from 'config';
import ConfirmModal from './ConfirmModal/ConfirmModal';
import uuid from 'helpers/uuid';
import { Timer } from './Timer/Timer';
import RadioField from './RadioField';

class LoginForm extends React.Component {
  state = {
    isVerified: false,
    loading: false,
    error: false,
    recaptchaToken: '',
    showVerificationCodeModal: false,
    selectCodeMode: false,
    showMdnInput: false,
    needCode: false,
    needMdn: false,
    mdnValid: false,
    emptyVerificationCodeError: false,
    timerTime: null,
  };

  componentDidMount() {
    const { dispatch } = this.props;

    this.formName = 'login';
    this.recaptchaTokenKey = 'recaptcha_token';
    this.recaptchaInstance = null;
    const deviceKey = '__device_uuid';
    this.deviceUuid = window.localStorage.getItem(deviceKey);
    if (!this.deviceUuid) {
      this.deviceUuid = uuid();
      window.localStorage.setItem(deviceKey, this.deviceUuid);
    }

    // add device uuid to request
    const deviceUuidField = 'device_uuid';
    dispatch(registerField(this.formName, deviceUuidField, 'Field'));
    dispatch(changeField(this.formName, deviceUuidField, this.deviceUuid));
  }

  clearCodeError = () => {
    this.setState({ emptyVerificationCodeError: false });
  };

  captchaHandleSubmit = (e) => {
    e.preventDefault();
    e.persist();

    const { formValues } = this.props;
    const {
      needCode,
      needMdn,
      mdnValid,
      selectCodeMode,
      showVerificationCodeModal,
      recaptchaToken,
    } = this.state;

    if (formValues.code_sending_mode === 'sms' && !formValues.target_mdn) {
      this.setState({
        selectCodeMode: true,
        showMdnInput: true,
        needMdn: true,
      });
      return;
    }

    if (needMdn && !mdnValid && !selectCodeMode) {
      this.setState({
        selectCodeMode: true,
        showMdnInput: false,
      });

      return;
    }

    if (
      (formValues.code_sending_mode === 'email' &&
        !showVerificationCodeModal &&
        !selectCodeMode) ||
      (needMdn && mdnValid && !showVerificationCodeModal)
    ) {
      this.setState({ showVerificationCodeModal: true });
      return;
    }

    if (needCode && !showVerificationCodeModal && !selectCodeMode) {
      if (recaptchaToken) {
        this.verifyCallback(recaptchaToken);
      } else {
        this.setState({ showVerificationCodeModal: true });
      }

      return;
    }

    if (recaptchaToken) {
      this.verifyCallback(recaptchaToken);
    } else if (this.recaptchaInstance) {
      this.recaptchaInstance.execute();
    }
  };

  // response it's recaptcha token
  verifyCallback = (recaptchaToken) => {
    const {
      dispatch,
      handleSubmit,
      onLogin,
      formValues,
      formRegisteredFields,
    } = this.props;
    if (recaptchaToken) {
      this.setState({
        isVerified: true,
        recaptchaToken: recaptchaToken,
      });

      const isRegisteredVerificationCode =
        formRegisteredFields && formRegisteredFields.verification_code;
      const verificationCode = formValues && formValues.verification_code;
      if (isRegisteredVerificationCode && !verificationCode) {
        this.setState({ emptyVerificationCodeError: true });
        return;
      } else {
        this.setState({ emptyVerificationCodeError: false });
      }

      dispatch(registerField(this.formName, this.recaptchaTokenKey, 'Field'));
      dispatch(
        changeField(this.formName, this.recaptchaTokenKey, recaptchaToken),
      );
      this.setState({ loading: true, error: false });
      setTimeout(() => {
        if (!recaptchaToken) {
          this.setState({ error: true }); // token expired
          return;
        }
        handleSubmit().then((response) => {
          if (response && response.needCode) {
            this.setState({
              loading: false,
              showVerificationCodeModal: true,
              needCode: true,
              selectCodeMode: false,
              showMdnInput: false,
              mdnValid: true,
              timerTime: response.timerTime,
            });
          } else if (response && response.selectCodeMode) {
            this.setState({
              loading: false,
              selectCodeMode: true,
              needCode: true,
            });
          } else {
            this.setState({ loading: false });
            onLogin();
          }
        });
      }, 0);
    } else {
      this.setState({
        error: true,
      });
    }
  };

  handleCodeChange = (e) => {
    this.clearCodeError();
  };

  closeVerificationModal = () => {
    const { dispatch } = this.props;

    this.setState({
      showVerificationCodeModal: false,
      selectCodeMode: false,
      showMdnInput: false,
      needMdn: false,
      mdnValid: false,
      needCode: false,
      emptyVerificationCodeError: false,
      timerTime: null,
    });

    // reset mdn and code sending mode
    dispatch(changeField(this.formName, 'target_mdn', ''));
    dispatch(changeField(this.formName, 'code_sending_mode', ''));
    dispatch(changeField(this.formName, 'verification_code', ''));
    dispatch(unregisterField(this.formName, 'verification_code'));
  };

  expiredCallback = () => {
    const { dispatch } = this.props;
    this.setState({
      isVerified: false,
      recaptchaToken: '',
    });
    dispatch(changeField(this.formName, this.recaptchaTokenKey, ''));
  };

  recaptchaLoaded = () => {
    console.log('captcha successfully loaded');
  };

  onEnd = () => {
    window.location.reload(false);
  };

  render() {
    const { isLoggedIn, user, error, onLogin } = this.props;
    const {
      loading,
      showVerificationCodeModal,
      showMdnInput,
      selectCodeMode,
      emptyVerificationCodeError,
      timerTime,
    } = this.state;
    if (isLoggedIn) {
      typeof onLogin === 'function' && onLogin();
      return (
        <div>
          Logged in as {user.first_name} {user.last_name}
        </div>
      );
    }

    return (
      <React.Fragment>
        <Spinner
          showLoader={loading && !selectCodeMode && !showVerificationCodeModal}
        />

        <Recaptcha
          ref={(e) => (this.recaptchaInstance = e)}
          sitekey={recapthcaKey}
          size="invisible"
          render="explicit"
          badge="bottomleft"
          verifyCallback={this.verifyCallback}
          expiredCallback={this.expiredCallback}
          onloadCallback={this.recaptchaLoaded}
          elementID="g-recaptcha"
        />
        <ConfirmModal
          onConfirm={this.captchaHandleSubmit}
          onClose={this.closeVerificationModal}
          confirmBtnText="Send code"
          cancelBtnText="Close"
          isOpen={selectCodeMode}
          closeOnConfirmClick={false}
          className="login-form"
          description={
            showMdnInput ? (
              <React.Fragment>
                <span style={{ fontSize: '20px' }}>
                  Please enter your Wing phone number to receive a verification
                  code.
                </span>
                <br />
                <br />
                <span style={{ display: 'block', maxWidth: '300px' }}>
                  <Field
                    component={InputField}
                    name="target_mdn"
                    label="Phone Number"
                  />
                </span>
                {error && <FormError error={error} />}
                <Spinner showLoader={loading} styles={{ left: '0' }} />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <span style={{ fontSize: '20px' }}>
                  We’ll send a verification code to the phone number or email
                  linked to your account. Select below.
                </span>
                <br />
                <br />
                <Field
                  component={RadioField}
                  name="code_sending_mode"
                  label="Text me the code"
                  radioValue="sms"
                />
                <Field
                  component={RadioField}
                  name="code_sending_mode"
                  label="Email me the code"
                  radioValue="email"
                />
                <Spinner showLoader={loading} styles={{ left: '0' }} />
              </React.Fragment>
            )
          }
          title="This device isn’t recognized."
        />
        <ConfirmModal
          onConfirm={this.captchaHandleSubmit}
          onClose={this.closeVerificationModal}
          closeOnConfirmClick={false}
          confirmBtnText="Verify"
          cancelBtnText="Close"
          className="login-form"
          isOpen={showVerificationCodeModal && !selectCodeMode}
          description={
            <React.Fragment>
              <span style={{ fontSize: '20px' }}>
                We just sent a verification code to your&nbsp;
                {this.props.formValues.code_sending_mode === 'email'
                  ? 'email'
                  : 'phone number'}
                &nbsp;to verify your identity.
              </span>
              <br />
              Please enter the code below to access your Wing account.
              <br />
              <Field
                component={InputField}
                onChange={this.handleCodeChange}
                name="verification_code"
                label="Verification code"
              />
              {emptyVerificationCodeError && (
                <p style={{ fontSize: '14px' }} className="text-danger">
                  Required
                </p>
              )}
              <br />
              The verification code will expire in{' '}
              {!loading && timerTime && (
                <Timer seconds={timerTime} onEnd={this.onEnd} />
              )}{' '}
              minutes.
              <br />
              <Spinner showLoader={loading} styles={{ left: '0' }} />
              {error && <FormError error={error} />}
            </React.Fragment>
          }
          title="This device isn’t recognized."
        />

        <form onSubmit={this.captchaHandleSubmit}>
          <Field
            component={InputField}
            autoComplete="username"
            name="email"
            label="Email"
          />
          <Field
            component={InputField}
            autoComplete="current-password"
            name="password"
            label="Password"
            type="password"
          />
          {error && <FormError error={error} />}
          <button className="btn btn-default">Sign in</button>
        </form>
      </React.Fragment>
    );
  }
}

const validate = (formValues) => {
  return {};
};

LoginForm = reduxForm({
  form: 'login',
  validate,
})(LoginForm);

export default LoginForm;
